import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getCityData, type CityData } from '../../utils/cityDataLocal';

interface CityIntroductionProps {
  cityName: string;
  timezone?: string;
  className?: string;
}

export default function CityIntroduction({ cityName, timezone, className = '' }: CityIntroductionProps) {
  const { i18n, t } = useTranslation(['worldclock']);
  const [cityInfo, setCityInfo] = useState<CityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timezoneData, setTimezoneData] = useState<{
    offset: number;
    isDST: boolean;
    standardOffset: number;
    dstOffset: number;
  } | null>(null);

  // 获取时区缩写
  const getTimezoneAbbreviation = (timezone: string, isDST: boolean) => {
    const timezoneMap: Record<string, { standard: string; dst: string }> = {
      'America/New_York': { standard: 'EST', dst: 'EDT' },
      'America/Chicago': { standard: 'CST', dst: 'CDT' },
      'America/Denver': { standard: 'MST', dst: 'MDT' },
      'America/Los_Angeles': { standard: 'PST', dst: 'PDT' },
      'Europe/London': { standard: 'GMT', dst: 'BST' },
      'Europe/Paris': { standard: 'CET', dst: 'CEST' },
      'Europe/Berlin': { standard: 'CET', dst: 'CEST' },
      'Asia/Tokyo': { standard: 'JST', dst: 'JST' },
      'Asia/Shanghai': { standard: 'CST', dst: 'CST' },
      'Asia/Singapore': { standard: 'SGT', dst: 'SGT' },
      'Australia/Sydney': { standard: 'AEST', dst: 'AEDT' },
    };

    const abbreviations = timezoneMap[timezone] || { standard: 'UTC', dst: 'UTC' };
    return isDST ? abbreviations.dst : abbreviations.standard;
  };

  // 计算时区信息
  useEffect(() => {
    if (timezone) {
      const calculateTimezoneInfo = () => {
        try {
          const now = new Date();
          
          // 获取UTC时间
          const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
          
          // 获取目标时区时间
          const targetTime = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
          
          // 计算偏移量（小时）
          const offset = (targetTime.getTime() - utcTime.getTime()) / (1000 * 60 * 60);
          
          // 检测是否使用夏令时
          const janDate = new Date(now.getFullYear(), 0, 1);
          const julDate = new Date(now.getFullYear(), 6, 1);
          
          const janOffset = (new Date(janDate.toLocaleString('en-US', { timeZone: timezone })).getTime() - 
                           new Date(janDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime()) / (1000 * 60 * 60);
          
          const julOffset = (new Date(julDate.toLocaleString('en-US', { timeZone: timezone })).getTime() - 
                            new Date(julDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime()) / (1000 * 60 * 60);
          
          const isDST = Math.abs(offset - julOffset) < Math.abs(offset - janOffset);
          const standardOffset = isDST ? offset - 1 : offset;
          const dstOffset = isDST ? offset : offset + 1;
          
          setTimezoneData({
            offset,
            isDST,
            standardOffset,
            dstOffset
          });
        } catch (error) {
          console.error('Error calculating timezone info:', error);
        }
      };

      calculateTimezoneInfo();
    }
  }, [timezone]);

  useEffect(() => {
    const fetchCityInfo = async () => {
      try {
        setLoading(true);
        setError(null);

        // 从本地 CSV 数据获取城市信息
        const data = await getCityData(cityName);

        if (data) {
          setCityInfo(data);
        } else {
          setError('City information not found');
        }
      } catch (err) {
        setError('Failed to load city information');
        console.error('Error fetching city info:', err);
      } finally {
        setLoading(false);
      }
    };

    if (cityName) {
      fetchCityInfo();
    }
  }, [cityName, i18n.language]);

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-lg p-8 ${className}`}>
        <div className="animate-pulse">
          <div className="flex space-x-4">
            <div className="w-32 h-24 bg-gray-300 rounded-lg"></div>
            <div className="flex-1 space-y-2">
              <div className="h-6 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-full"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              <div className="h-4 bg-gray-300 rounded w-4/5"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !cityInfo) {
    return null; // 不显示错误状态，静默失败
  }

  // 根据当前语言选择显示内容
  const isZh = i18n.language === 'zh';
  const displayName = isZh ? cityInfo.cityNameZh : cityInfo.cityNameEn;
  const displayDescription = isZh ? cityInfo.descriptionZh : cityInfo.descriptionEn;
  const displayCountry = isZh ? cityInfo.countryZh : cityInfo.countryEn;

  return (
    <div className={`bg-white rounded-2xl shadow-lg p-8 ${className}`}>
      {/* 城市标题 - 与Popular Cities标题对齐 */}
      <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 text-left">
        {displayName}
        {cityInfo.alias && (
          <span className="text-sm text-gray-500 ml-2">({cityInfo.alias})</span>
        )}
      </h3>

      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
        {/* 城市图片 - 使用本地图片 */}
        {cityInfo.imagePath && (
          <div className="w-full md:w-48 h-48 md:h-32 flex-shrink-0">
            <img
              src={cityInfo.imagePath}
              alt={displayName}
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                // 图片加载失败时隐藏图片容器
                const target = e.target as HTMLImageElement;
                if (target.parentElement) {
                  target.parentElement.style.display = 'none';
                }
              }}
            />
          </div>
        )}

        {/* 城市信息 */}
        <div className="flex-1">

          {/* 时区信息 */}
          {timezoneData && timezone && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">{t('timezone.title', { city: displayName })}</h4>
              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span className="font-medium">{t('timezone.localClockOffset')}:</span>
                  <span>{t('timezone.today')}, {timezoneData.offset >= 0 ? '+' : ''}{timezoneData.offset} {t('timezone.hours')}</span>
                </div>

                <div className="flex justify-between">
                  <span className="font-medium">{t('timezone.timeZone')}:</span>
                  <span>(UTC/GMT {timezoneData.offset >= 0 ? '+' : ''}{timezoneData.offset}:00) {timezone}</span>
                </div>

                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between">
                    <span className="font-medium">{t('timezone.standardTimeZone')}:</span>
                    <span>{getTimezoneAbbreviation(timezone, false)} = UTC{timezoneData.standardOffset >= 0 ? '+' : ''}{timezoneData.standardOffset}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="font-medium">{t('timezone.daylightSavingTimeZone')}:</span>
                    <span>{getTimezoneAbbreviation(timezone, true)} = UTC{timezoneData.dstOffset >= 0 ? '+' : ''}{timezoneData.dstOffset}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <p className="text-gray-600 text-sm leading-relaxed">
            {displayDescription}
          </p>

          {/* 数据来源标识 */}
          <div className="mt-4 flex items-center text-xs text-gray-400">
            <i className="ri-information-line mr-1"></i>
            <span>source: </span>
            <a
              href={`https://${isZh ? 'zh' : 'en'}.wikipedia.org/wiki/${encodeURIComponent(displayName)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 text-blue-500 hover:text-blue-700"
            >
              wikipedia
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}